import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { X } from "lucide-react";
import { useRecentTools } from "@/hooks/use-recent-tools";
import { useHaptics } from "@/hooks/use-haptics";
import { navGroups } from "@/lib/nav-tree";
import { pathOfNav, useNavActive } from "@/components/SideNav";
import type { NavItem } from "@/lib/nav-items";

interface Props {
  open: boolean;
  onClose: () => void;
}

/**
 * The full nav tree on a phone — same rows as the desktop sidebar, in a
 * bottom sheet that clears the tab bar and the home indicator.
 */
const MobileNavDrawer = ({ open, onClose }: Props) => {
  const navigate = useNavigate();
  const isActive = useNavActive();
  const { trackVisit } = useRecentTools();
  const { lightTap } = useHaptics();

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const go = (item: NavItem) => {
    lightTap();
    trackVisit(pathOfNav(item), item.label);
    navigate(item.path);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[60] md:hidden" onClick={onClose}>
      <div className="absolute inset-0 bg-background/60 backdrop-blur-sm" />
      <div
        className="absolute inset-x-0 bottom-0 max-h-[80vh] overflow-y-auto rounded-t-2xl border-t border-border bg-card shadow-lg animate-fade-in"
        style={{ paddingBottom: "calc(4.5rem + env(safe-area-inset-bottom))" }}
        role="dialog"
        aria-modal="true"
        aria-label="Menu"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-border bg-card px-4 py-3">
          <span className="text-sm font-bold tracking-tight">Trades Toolbelt</span>
          <button
            onClick={onClose}
            className="rounded-md p-1.5 text-muted-foreground hover:bg-secondary"
            aria-label="Close menu"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="p-2">
          {navGroups.map((group, gi) => (
            <div key={group.label || gi}>
              {group.label && (
                <div className="px-3 pt-4 pb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground/70">
                  {group.label}
                </div>
              )}
              {group.rows.map(({ item, nested }) => {
                const active = isActive(item);
                return (
                  <button
                    key={item.path}
                    onClick={() => go(item)}
                    aria-current={active ? "page" : undefined}
                    className={`flex w-full items-center gap-3 rounded-lg py-3 text-left transition-colors ${
                      nested ? "pl-8 pr-3" : "px-3"
                    } ${active ? "bg-primary/10 text-primary" : "text-foreground hover:bg-secondary"}`}
                  >
                    <item.icon className="h-5 w-5 shrink-0" />
                    <span className="min-w-0">
                      <span className="block truncate text-sm font-medium">{item.label}</span>
                      <span className="block truncate text-[11px] text-muted-foreground">{item.desc}</span>
                    </span>
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default MobileNavDrawer;
